import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Link2, 
  CheckCircle2,
  Zap,
  Shield,
  Clock,
  BarChart3,
  RefreshCw,
  AlertCircle,
  Settings,
  Layers,
  Database,
  Cloud,
  Play,
  Star,
  ArrowUpRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const Product = () => {
  const features = [
    {
      icon: RefreshCw,
      title: 'Real-Time Sync',
      description: 'Automatic synchronization of orders, inventory, and customer data across ecommerce and ERP platforms.',
      color: 'from-blue-500 to-indigo-600',
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Process thousands of transactions per minute with our optimized data pipeline.',
      color: 'from-yellow-500 to-orange-600',
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption and security protocols to protect your sensitive business data.',
      color: 'from-green-500 to-emerald-600',
    },
    {
      icon: AlertCircle,
      title: 'Smart Error Handling',
      description: 'Intelligent error detection and automatic recovery to ensure data integrity.',
      color: 'from-red-500 to-pink-600',
    },
    {
      icon: Settings,
      title: 'Custom Mapping',
      description: 'Flexible field mapping to match your unique business processes and workflows.',
      color: 'from-purple-500 to-violet-600',
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Real-time monitoring and analytics to track sync performance and data flow.',
      color: 'from-cyan-500 to-teal-600',
    },
  ];

  const integrationFlows = [
    {
      title: 'Orders',
      description: 'Automatically sync orders from ecommerce platforms to ERP systems',
      direction: 'Multi-directional',
    },
    {
      title: 'Inventory',
      description: 'Real-time inventory level updates across all platforms',
      direction: 'Bi-directional',
    },
    {
      title: 'Customers',
      description: 'Sync customer records and maintain a unified customer database',
      direction: 'Multi-directional',
    },
    {
      title: 'Products',
      description: 'Keep product catalogs synchronized with pricing and descriptions',
      direction: 'Bi-directional',
    },
    {
      title: 'Fulfillment',
      description: 'Update ecommerce platforms with fulfillment and tracking info',
      direction: 'Multi-directional',
    },
    {
      title: 'Refunds',
      description: 'Process refunds across all connected platforms seamlessly',
      direction: 'Multi-directional',
    },
  ];

  const benefits = [
    'Eliminate manual data entry',
    'Reduce errors by 95%',
    'Save 20+ hours per week',
    'Real-time data accuracy',
    'Scalable architecture',
    '99.9% uptime guarantee',
  ];

  const pricingTiers = [
    {
      name: 'Starter',
      price: '$299',
      period: '/month',
      description: 'Perfect for small businesses getting started',
      features: [
        'Up to 1,000 orders/month',
        'Order synchronization',
        'Inventory sync',
        'Email support',
        'Basic analytics',
      ],
      highlighted: false,
    },
    {
      name: 'Professional',
      price: '$499',
      period: '/month',
      description: 'For growing businesses with higher volume',
      features: [
        'Up to 10,000 orders/month',
        'All Starter features',
        'Custom field mapping',
        'Priority support',
        'Advanced analytics',
        'API access',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For large-scale operations',
      features: [
        'Unlimited orders',
        'All Professional features',
        'Dedicated account manager',
        '24/7 phone support',
        'Custom integrations',
        'SLA guarantee',
      ],
      highlighted: false,
    },
  ];

  return (
    <div data-testid="product-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 hero-gradient mesh-gradient overflow-hidden">
        <div className="absolute top-20 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 border border-purple-200 mb-6">
                <Star className="w-4 h-4 text-purple-600 fill-purple-600" />
                <span className="text-sm font-medium text-purple-700">AI-Powered Integration Platform</span>
              </div>
              
              <h1 
                data-testid="product-hero-title"
                className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tighter mb-6"
              >
                <span className="gradient-text">Setu Connect</span>
                <br />
                <span className="text-slate-900">AI-Powered ERP Integration</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
                The AI-powered integration platform that intelligently connects your ecommerce channels 
                to your ERP system. With machine learning capabilities, it learns from your data patterns to optimize synchronization. 
                Support for Shopify, Faire, Walmart, Amazon, MarketTime and NetSuite, Zoho. Automate your operations with AI-driven insights.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/contact">
                  <Button 
                    data-testid="product-cta-primary"
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-blue-500/25 group"
                  >
                    Request Demo
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Button 
                  data-testid="product-cta-secondary"
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg font-semibold border-slate-200 hover:bg-slate-50 group"
                >
                  <Play className="mr-2 w-5 h-5 text-blue-600" />
                  Watch Video
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Free 14-day trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-500" />
                  <span>No credit card required</span>
                </div>
              </div>
            </div>

            {/* Product Visual */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl" />
              <Card className="relative bg-white rounded-3xl shadow-2xl border-0 overflow-hidden">
                <CardContent className="p-8">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                        <Link2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                      <h4 className="font-bold text-slate-900">Setu Connect AI Dashboard</h4>
                      <p className="text-sm text-slate-500">AI-powered multi-platform sync</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      Live
                    </span>
                  </div>

                  {/* Platforms */}
                  <div className="space-y-4 mb-8">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Ecommerce Platforms</p>
                      <div className="grid grid-cols-3 gap-2">
                        {['Shopify', 'Faire', 'Walmart', 'Amazon', 'MarketTime', 'Tiktok'].map((platform, i) => (
                          <div key={i} className="flex items-center justify-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <span className="text-sm font-medium text-slate-700">{platform}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-center py-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                        <ArrowRight className="w-4 h-4 text-white rotate-90" />
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-3">ERP Systems</p>
                      <div className="grid grid-cols-2 gap-2">
                        {['NetSuite', 'Zoho'].map((erp, i) => (
                          <div key={i} className="flex items-center justify-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <span className="text-sm font-medium text-slate-700">{erp}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  {/* <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-100">
                    <div className="text-center">
                      <p className="text-2xl font-bold font-['Outfit'] text-slate-900 font-mono-numbers">1,247</p>
                      <p className="text-xs text-slate-500">Orders Today</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold font-['Outfit'] text-slate-900 font-mono-numbers">99.9%</p>
                      <p className="text-xs text-slate-500">Success Rate</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold font-['Outfit'] text-green-600 font-mono-numbers">0</p>
                      <p className="text-xs text-slate-500">Errors</p>
                    </div>
                  </div> */}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-8 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2 text-white">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section data-testid="features-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Features
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              Everything You Need for{' '}
              <span className="gradient-text">Seamless Integration</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Setu Connect comes packed with powerful features designed to streamline your operations across all platforms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index}
                data-testid={`feature-card-${index}`}
                className="group bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-xl hover:border-blue-100 transition-all card-hover"
              >
                <CardContent className="p-0">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold font-['Outfit'] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Flows */}
      <section data-testid="integration-flows" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-purple-50 text-purple-600 text-sm font-medium mb-4">
              Data Flows
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              What Gets <span className="gradient-text">Synchronized</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Setu Connect handles all critical data flows between your ecommerce platforms and ERP systems.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrationFlows.map((flow, index) => (
              <Card 
                key={index}
                data-testid={`flow-card-${index}`}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-0">
                  <div className="flex items-start justify-between mb-4">
                    <h4 className="text-lg font-bold font-['Outfit']">{flow.title}</h4>
                    <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-medium">
                      {flow.direction}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm">{flow.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section data-testid="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              How It Works
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight">
              Get Started in <span className="gradient-text">3 Simple Steps</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                icon: Cloud,
                title: 'Connect',
                description: 'Connect your Shopify store and NetSuite account with our secure OAuth integration.',
              },
              {
                step: '2',
                icon: Settings,
                title: 'Configure',
                description: 'Set up your field mappings and customize sync rules to match your business needs.',
              },
              {
                step: '3',
                icon: Zap,
                title: 'Automate',
                description: 'Sit back and let Setu Connect handle all your data synchronization automatically.',
              },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mx-auto">
                    <item.icon className="w-10 h-10 text-white" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-sm font-bold text-blue-600">
                    {item.step}
                  </span>
                </div>
                <h4 className="text-xl font-bold font-['Outfit'] mb-3">{item.title}</h4>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="pricing-section" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Pricing
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              Simple, Transparent <span className="gradient-text">Pricing</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the plan that fits your business. All plans include a 14-day free trial.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <Card 
                key={index}
                data-testid={`pricing-tier-${index}`}
                className={`rounded-3xl overflow-hidden transition-all ${
                  tier.highlighted 
                    ? 'bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-2xl scale-105 border-0' 
                    : 'bg-white border border-slate-100 hover:shadow-xl'
                }`}
              >
                <CardContent className="p-8">
                  {tier.highlighted && (
                    <span className="inline-block px-3 py-1 rounded-full bg-blue-500 text-white text-xs font-medium mb-4">
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-2xl font-bold font-['Outfit'] mb-2">{tier.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold font-['Outfit']">{tier.price}</span>
                    <span className={tier.highlighted ? 'text-slate-300' : 'text-slate-500'}>
                      {tier.period}
                    </span>
                  </div>
                  <p className={`mb-6 ${tier.highlighted ? 'text-slate-300' : 'text-slate-600'}`}>
                    {tier.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle2 className={`w-5 h-5 ${tier.highlighted ? 'text-blue-400' : 'text-green-500'}`} />
                        <span className={tier.highlighted ? 'text-slate-300' : 'text-slate-600'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button 
                      className={`w-full rounded-full py-6 font-semibold ${
                        tier.highlighted 
                          ? 'bg-white text-slate-900 hover:bg-slate-100' 
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90'
                      }`}
                    >
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 cta-gradient relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight text-white mb-6">
            Ready to Streamline Your Operations?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Start your 14-day free trial today and see how Setu Connect can transform your operations.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button 
                data-testid="product-cta-bottom"
                size="lg"
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl group"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-lg font-semibold text-white border-white/30 hover:bg-white/10"
              >
                Schedule Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Product;
